/**
 * SOLID Review
 * S — Good. One concern: bringing the client's table level with the server's when it may have missed
 *     something — a pushed table view, a reconnect's resync, the lobby's reads. They share one mapping
 *     (the active round onto the table), which is why they share a file.
 * O — Good. A new divergence is one more guarded branch in `decideTableView`; the mapping is one place.
 * L — N/A — functions.
 * I — Good. Each decision takes the table and its own answer.
 * D — Strong. The draft helpers, the moved reveal and roster rules, and the schema types.
 */
import { GamePhase } from '../../uml-components/primitives';
import { decided, refuse, REFUSAL, type IEntityDecision, type TDecisionResult } from '../shared';
import { settle, freshLives, revealOf, challengerOf } from './tableDraft';
import { diceRollFromReveal } from '../../game/RevealTransforms';
import { buildRosterFromRaw } from '../../game/Roster';
import type { IPropsClientTableInput, IStateClientTable } from '../../ports/schemas/ClientTable';
import type { IPropsClientChanges } from '../../ports/schemas/ClientChanges';
import type { IPayloadTableView, IPayloadActiveRound, IEntityTableAck, IEntityTableViewOutcome } from '../../ports/schemas/TableEvents';
import type {
  ICommandNothing, ICommandActiveRoundRestored, ICommandSuspendedReconciled, ICommandLobbyActiveRound, ICommandLobbyRoomLoaded,
  IPayloadResync, IPayloadRoomRead,
} from '../../ports/schemas/TableMoves';

type TAck = TDecisionResult<IEntityDecision<IPropsClientChanges, IEntityTableAck>>;

const NOTHING: IPropsClientChanges = {};

function ack(base: IStateClientTable, next: IStateClientTable): TAck {
  const dto: IEntityTableAck = { applied: true };
  return decided(settle(base, next), dto);
}

function ignored(): TAck {
  const dto: IEntityTableAck = { applied: false };
  return decided(NOTHING, dto);
}

/**
 * THE ACTIVE ROUND ONTO THE TABLE — `applyActiveRoundSnapshot` (application/use-cases/useGameStateRestore.ts),
 * moved. Lives first, whatever the phase. Then the reveal, the pending claim, or the round's phase:
 *
 *   - no round at all: between rounds — sit in ROLLING and catch the next round's start;
 *   - a reveal: on screen for everyone, and REVEALING only for the loser, who holds the Roll First
 *     button; the winner waits in ROLLING for the next round;
 *   - a pending claim: DECIDING, with the server's decider and whether the claim went blind (§28.61),
 *     and the claim itself only on the decider's own table;
 *   - dice rolled by the local roller: PEEKING; anything else: ROLLING.
 *
 * Two of the store's steps happen here in one. A reveal used to wait in `pendingRevealResult` until
 * ControlPanel's effect copied it to the screen, with the reveal's dice, and only if no reveal was showing
 * already. A pending claim waited the same way in `pendingOpponentClaim`. Both are written directly now:
 * a newer reveal replaces the one on screen instead of queueing behind it. The queued copy was replayed by
 * the next round's start, which then put the previous reveal back on screen (deviation, flagged in §28).
 *
 * `fallbackPlayerIds` seeds full lives when the server lists none. `me` is compared as the callers did:
 * the reconnect passed the player's id, the pushed view passed `localPlayerId ?? ''`.
 */
export function applyActiveRound(next: IStateClientTable, ar: IPayloadActiveRound | null, me: string, fallbackPlayerIds: ReadonlyArray<string>): void {
  next.playerLives = ar !== null && ar.playerLives.length > 0
    ? Object.fromEntries(ar.playerLives.map((e) => [e.playerId, e.lives]))
    : freshLives(fallbackPlayerIds);
  if (ar === null) {
    next.phase = GamePhase.ROLLING;
    return;
  }
  if (ar.revealResult !== null) {
    next.revealResult = revealOf(ar.revealResult);
    next.lastDiceRoll = diceRollFromReveal(ar.revealResult);
    next.phase = ar.revealResult.losingPlayerId === me ? GamePhase.REVEALING : GamePhase.ROLLING;
    return;
  }
  next.activeRoundId = ar.roundId;
  next.activeRollerId = ar.rollerId;
  next.minimumClaimScore = ar.minimumScore ?? 0;
  const pending = ar.pendingClaim;
  if (pending !== null) {
    next.lastClaimedScore = pending.claimedScore;
    next.claimWasBlind = pending.wasBlind;
    next.activeChallengerId = challengerOf(pending.challengerId);
    if (pending.challengerId === me) next.opponentClaim = { score: pending.claimedScore, label: pending.label };
    next.phase = GamePhase.DECIDING;
  } else if (ar.diceRolled && ar.rollerId === me) {
    next.phase = GamePhase.PEEKING;
  } else {
    next.phase = GamePhase.ROLLING;
  }
}

const SHOWING_REVEAL: readonly GamePhase[] = Object.freeze([GamePhase.REVEALING, GamePhase.GAME_OVER]);

function sameSet(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

/**
 * THE TABLE CORRECTS THE CLIENT — `reconcileTableView` (application/use-cases/reconcileTableView.ts) and
 * `useTableView`'s version bookkeeping, moved (backend §28.192). The server pushes the whole table after
 * every save, AFTER the move's own events. A view for another room is ignored; one at or below the version
 * already applied is stale — two replicas can deliver out of order. Otherwise:
 *
 *   - FACTS always: each seat's lives, and who is suspended — idempotent to set;
 *   - a CLOSED table the client still plays at: the room has ended (not in the lobby);
 *   - a MISSED MOVE only on divergence: a live round the client does not have, a claim it has not seen, or
 *     a reveal while it still shows play.
 *
 * When the client heard the move's event — the normal case, since the view comes after it — nothing but
 * the facts is touched: the phases the events drive stay theirs.
 */
export function decideTableView(
  input: IPropsClientTableInput,
  view: IPayloadTableView,
): TDecisionResult<IEntityDecision<IPropsClientChanges, IEntityTableViewOutcome>> {
  const { table } = input;
  const outcome = (o: IEntityTableViewOutcome['outcome'], changes: IPropsClientChanges) => {
    const dto: IEntityTableViewOutcome = { outcome: o };
    return decided(changes, dto);
  };
  const room = table.room;
  if (room === null || view.roomId !== room.roomId) return outcome('ignored', NOTHING);
  const lastVersion = table.tableVersion !== null && table.tableVersion.roomId === view.roomId ? table.tableVersion.version : 0;
  if (view.version <= lastVersion) return outcome('stale', NOTHING);

  const next: IStateClientTable = { ...table, tableVersion: { roomId: view.roomId, version: view.version } };
  const ar = view.activeRound;
  if (ar !== null && ar.playerLives.length > 0 && ar.playerLives.some((p) => table.playerLives[p.playerId] !== p.lives)) {
    next.playerLives = Object.fromEntries(ar.playerLives.map((p) => [p.playerId, p.lives]));
  }
  if (!sameSet(view.suspendedPlayerIds, table.suspendedPlayerIds)) next.suspendedPlayerIds = [...view.suspendedPlayerIds];

  if (view.status === 'FINISHED') {
    if (table.phase !== GamePhase.LOBBY) next.roomEnded = true;
    return outcome('reconciled', settle(table, next));
  }
  if (ar === null) return outcome('in-sync', settle(table, next));

  const missedRound = ar.roundId !== null && ar.roundId !== table.activeRoundId;
  const missedClaim = ar.roundId !== null && ar.pendingClaim !== null && ar.pendingClaim.claimedScore !== table.lastClaimedScore;
  const missedReveal = ar.roundId === null && ar.revealResult !== null && !SHOWING_REVEAL.includes(table.phase);
  if (!missedRound && !missedClaim && !missedReveal) return outcome('in-sync', settle(table, next));

  if (missedReveal && ar.revealResult !== null) {
    // As the cup-lift event does for every seat: the reveal on screen, the game over when the table says so.
    next.revealResult = revealOf(ar.revealResult);
    next.lastDiceRoll = diceRollFromReveal(ar.revealResult);
    next.phase = ar.revealResult.endsGame === true ? GamePhase.GAME_OVER : GamePhase.REVEALING;
    return outcome('reconciled', settle(table, next));
  }
  applyActiveRound(next, ar, table.localPlayerId ?? '', room.players.map((p) => p.id));
  return outcome('reconciled', settle(table, next));
}

/** The phases a resync catches up: live play. The lobby, idle and game over have no round to miss. */
const LIVE_PLAY: readonly GamePhase[] = Object.freeze([
  GamePhase.ROLLING, GamePhase.PEEKING, GamePhase.CLAIMING, GamePhase.DECIDING, GamePhase.REVEALING,
]);

/**
 * A RECONNECT'S CATCH-UP — `useResyncActiveRound`, moved, in three decisions around two reads. A socket that
 * comes back delivers NEW events, but the moves others made while this tab was away are never replayed, so
 * the active round is read and mapped back on, silently, and then the room's suspended list, which live
 * events maintain only additively — exactly the events a drop can miss (§23.26).
 */
export function decidePlanResync(input: IPropsClientTableInput, _command: ICommandNothing): TDecisionResult<IEntityDecision<IPropsClientChanges, IPayloadResync>> {
  const { table } = input;
  if (table.room === null || table.localPlayerId === null) return refuse('No table to catch up.', REFUSAL.NOT_READY);
  if (!LIVE_PLAY.includes(table.phase)) return refuse(`Nothing to catch up in ${table.phase}.`, REFUSAL.NOT_IN_PLAY);
  const request: IPayloadResync = { roomId: table.room.roomId, roomCode: table.room.roomCode };
  return decided(NOTHING, request);
}

/**
 * The active round, read on reconnect, mapped on — and the decision countdown cleared: it belongs to
 * whatever round last ticked it, a reconnect may have missed both its last tick and the round's change, and
 * a still-live countdown refills within a second.
 *
 * Only a read that ANSWERED is applied. The hook mapped a failed read as "no round" — every seat back to
 * six lives, the phase to ROLLING — until the next pushed view corrected it; the shell now skips a failed
 * read, as the hook skipped a thrown one (deviation, flagged in §28). An answer for a table the player has
 * since left is ignored.
 */
export function decideActiveRoundRestored(input: IPropsClientTableInput, command: ICommandActiveRoundRestored): TAck {
  const { table } = input;
  if (table.room === null || table.room.roomId !== command.request.roomId || table.localPlayerId === null) return ignored();
  const next: IStateClientTable = { ...table };
  applyActiveRound(next, command.activeRound, table.localPlayerId, table.room.players.map((p) => p.id));
  next.decisionCountdown = null;
  return ack(table, next);
}

/** The room's own list of who is suspended replaces the additive one; nothing when the room is gone. */
export function decideSuspendedReconciled(input: IPropsClientTableInput, command: ICommandSuspendedReconciled): TAck {
  const { table } = input;
  if (table.room === null || table.room.roomId !== command.request.roomId) return ignored();
  if (command.suspendedPlayers === null || command.suspendedPlayers === undefined) return ignored();
  const next: IStateClientTable = {
    ...table,
    suspendedPlayerIds: command.suspendedPlayers.filter((p) => !p.isConnected).map((p) => p.id),
  };
  return ack(table, next);
}

/**
 * THE LOBBY'S READS — the roster on entry (`useLobbyRoomSync`) and the self-heal poll for a round that
 * started unheard (`useLobbyActiveRoundPoll`, every 2.5 s). Both ran only while LobbyPanel was mounted,
 * and dropped an answer that came back after it unmounted; here, after the table left the lobby.
 */
export function decidePlanLobbyRead(input: IPropsClientTableInput, _command: ICommandNothing): TDecisionResult<IEntityDecision<IPropsClientChanges, IPayloadRoomRead>> {
  const { table } = input;
  if (table.room === null) return refuse('No room to read.', REFUSAL.NOT_READY);
  if (table.phase !== GamePhase.LOBBY) return refuse('The table has left the lobby.', REFUSAL.NOT_IN_LOBBY);
  const request: IPayloadRoomRead = { roomId: table.room.roomId };
  return decided(NOTHING, request);
}

/** A round started that the lobby did not hear: roller, round, full lives, ROLLING. */
export function decideLobbyActiveRound(input: IPropsClientTableInput, command: ICommandLobbyActiveRound): TAck {
  const { table } = input;
  const room = table.room;
  if (room === null || room.roomId !== command.request.roomId || table.phase !== GamePhase.LOBBY) return ignored();
  const ar = command.activeRound;
  if (ar === null || !ar.roundId) return ignored();
  const next: IStateClientTable = { ...table, activeRoundId: ar.roundId, activeRollerId: ar.rollerId };
  if (room.players.length > 0) next.playerLives = freshLives(room.players.map((p) => p.id));
  next.phase = GamePhase.ROLLING;
  return ack(table, next);
}

/** The lobby's roster on entry: every seat fresh from the server's list. */
export function decideLobbyRoomLoaded(input: IPropsClientTableInput, command: ICommandLobbyRoomLoaded): TAck {
  const { table } = input;
  const room = table.room;
  if (room === null || room.roomId !== command.request.roomId || table.phase !== GamePhase.LOBBY) return ignored();
  if (command.roster.id !== room.roomId) return ignored();
  const next: IStateClientTable = { ...table, room: { ...room, players: buildRosterFromRaw(command.roster.players, table.localPlayerId) } };
  return ack(table, next);
}
